import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useCart } from '../context/CartContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)',
  border: '1px solid',
  borderColor: theme.palette.grey[100],
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
  },
}));

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderInfo, setOrderInfo] = useState(null);
  const { clearCart } = useCart();
  
  // Get session_id from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!sessionId) {
        setError('No order information available.');
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/api/checkout/session-status?sessionId=${sessionId}`);
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        setOrderInfo({
          orderId: sessionId.substring(0, 8).toUpperCase(),
          customerEmail: data.customer_email,
          shippingAddress: data.shipping?.address,
          paymentStatus: data.payment_status,
          totalAmount: data.amount_total
        });
        
        // Clear cart after successful order
        clearCart();
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Unable to load order details. Please contact customer support.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [sessionId, clearCart]);
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your order details...
        </Typography>
      </Container>
    );
  }
  
  if (error || !orderInfo) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Unable to retrieve order information.'}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<HomeIcon />} 
          onClick={() => navigate('/')}
        >
          Return to Home
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <StyledPaper elevation={0}>
        <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 3 }} />
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'grey.800' }}>
          Thank You For Your Order!
        </Typography>
        
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Order #{orderInfo.orderId}
        </Typography>
        
        <Box sx={{ my: 4, maxWidth: 600, mx: 'auto' }}>
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: 'success.main'
              }
            }}
          >
            We've sent a confirmation email to {orderInfo.customerEmail}.
          </Alert>
          
          <Typography paragraph align="center" sx={{ color: 'grey.700' }}>
            Your order has been placed and is being processed. You'll receive an email when your QR codes are ready for shipping.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 4, borderColor: 'grey.200' }} />
        
        {orderInfo.shippingAddress && (
          <Box sx={{ mb: 4, textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'grey.800' }}>
              Shipping Address
            </Typography>
            
            <Typography variant="body1">
              {orderInfo.shippingAddress.name}
            </Typography>
            <Typography variant="body1">
              {orderInfo.shippingAddress.line1}
              {orderInfo.shippingAddress.line2 && `, ${orderInfo.shippingAddress.line2}`}
            </Typography>
            <Typography variant="body1">
              {orderInfo.shippingAddress.city}, {orderInfo.shippingAddress.state} {orderInfo.shippingAddress.postal_code}
            </Typography>
            <Typography variant="body1">
              {orderInfo.shippingAddress.country}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'grey.800' }}>
            Payment Information
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <CreditCardIcon sx={{ color: 'grey.600' }} />
            <Typography variant="body1" sx={{ color: 'grey.700' }}>
              Paid with Credit Card via Stripe
            </Typography>
          </Box>
          
          {orderInfo.paymentStatus === 'paid' && (
            <Chip 
              label="Payment Successful" 
              color="success" 
              size="small" 
              sx={{ mt: 1 }}
              icon={<CheckCircleIcon />}
            />
          )}
          
          {orderInfo.totalAmount && (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
              Total Amount: £{orderInfo.totalAmount.toFixed(2)}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            size="large"
            sx={{ 
              px: 3, 
              py: 1.2, 
              borderRadius: 2, 
              boxShadow: '0 4px 10px rgba(0,0,0,0.07)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 15px rgba(0,0,0,0.1)',
              }
            }}
          >
            Return to Home
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<LocalShippingIcon />}
            onClick={() => navigate('/order-tracking')}
            size="large"
            sx={{ 
              px: 3, 
              py: 1.2, 
              borderRadius: 2,
              borderWidth: '1.5px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                borderWidth: '1.5px',
              }
            }}
          >
            Track Order
          </Button>
        </Box>
        
        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="grey.500">
            Need help with your order? Contact our support team at support@memorylink.com
          </Typography>
        </Box>
      </StyledPaper>
      
      <Box sx={{ mt: 5, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'grey.800' }}>
          What happens next?
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'grey.700', fontWeight: 600 }}>
                  1. Order Processing
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.600' }}>
                  We'll process your order and begin preparing your QR codes within 1 business day.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'grey.700', fontWeight: 600 }}>
                  2. QR Code Creation
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.600' }}>
                  Your unique QR codes will be created and physically produced with high-quality materials.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'grey.700', fontWeight: 600 }}>
                  3. Shipping
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.600' }}>
                  Your package will be shipped within 3-5 business days with tracking information sent to your email.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default OrderConfirmation;